import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import CameraAltRoundedIcon from "@mui/icons-material/CameraAltRounded";
import VideocamRoundedIcon from "@mui/icons-material/VideocamRounded";
import VideocamOffRoundedIcon from "@mui/icons-material/VideocamOffRounded";
import PreviewRoundedIcon from "@mui/icons-material/PreviewRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useSession } from "../../context/SessionContext";
import { useCameraApi } from "../../api/useCameraApi";
import { Camera, CAMERA_TYPES, CameraTags } from "../../state/types";
import { useSystemsContext } from "../../context/SystemsContext";

type CameraMode = "add" | "edit";
type HardwareTab = "cameras";
type CameraView = CAMERA_TYPES.EXTERIOR | CAMERA_TYPES.INTERIOR | "undercarriage";

const EMPTY_SNACK = { open: false, message: "", severity: "success" as "success" | "error" | "info" };

const isCameraInView = (camera: Camera, view: CameraView) => {
  if (view === CAMERA_TYPES.INTERIOR) {
    return camera.type === CAMERA_TYPES.INTERIOR;
  }

  if (view === "undercarriage") {
    return camera.type === CAMERA_TYPES.EXTERIOR && Boolean(camera.is_under_carriage);
  }

  return camera.type === CAMERA_TYPES.EXTERIOR && !camera.is_under_carriage;
};

const getCameraViewLabel = (view: CameraView) => {
  if (view === CAMERA_TYPES.INTERIOR) return "Interior";
  if (view === "undercarriage") return "Undercarriage";
  return "Exterior";
};

const createDraftCamera = (view: CameraView, cameras: Camera[]): Camera => {
  const type = view === CAMERA_TYPES.INTERIOR ? CAMERA_TYPES.INTERIOR : CAMERA_TYPES.EXTERIOR;
  const lastExteriorPosition = cameras
    .filter((camera) => camera.type === CAMERA_TYPES.EXTERIOR)
    .reduce((max, camera) => Math.max(max, Number(camera.camera_position) || 0), 0);

  return {
    cameraId: crypto.randomUUID(),
    type,
    brand: type === CAMERA_TYPES.EXTERIOR ? "axis" : "theta",
    camera_position: type === CAMERA_TYPES.EXTERIOR ? lastExteriorPosition + 1 : 0,
    ip_address: "",
    username: "",
    password: "",
    name: "",
    query_params: "",
    order: cameras.filter((camera) => camera.type === type).length + 1,
    tag: type === CAMERA_TYPES.EXTERIOR ? CameraTags.Unassigned : "",
    skip_ricoh_while_charging: false,
    is_under_carriage: view === "undercarriage",
  };
};

const getCameraId = (camera: Camera) => String(camera.cameraId ?? camera.id ?? "");

const sortCameras = (cameras: Camera[]) =>
  [...cameras].sort((left, right) => {
    if (left.type !== right.type) {
      return left.type === CAMERA_TYPES.EXTERIOR ? -1 : 1;
    }

    if (left.type === CAMERA_TYPES.EXTERIOR) {
      return (left.camera_position || 0) - (right.camera_position || 0);
    }

    const leftOrder = left.order || 0;
    const rightOrder = right.order || 0;
    if (leftOrder !== rightOrder) {
      return leftOrder - rightOrder;
    }

    return (left.name || "").localeCompare(right.name || "");
  });

const normalizeExteriorPositions = (cameras: Camera[]) => {
  const exterior = cameras
    .filter((camera) => camera.type === CAMERA_TYPES.EXTERIOR)
    .sort((left, right) => (left.camera_position || 0) - (right.camera_position || 0))
    .map((camera, index) => ({
      ...camera,
      camera_position: index + 1,
      order: index + 1,
    }));

  const interior = cameras
    .filter((camera) => camera.type === CAMERA_TYPES.INTERIOR)
    .sort((left, right) => (left.order || 0) - (right.order || 0))
    .map((camera, index) => ({
      ...camera,
      order: index + 1,
    }));

  return sortCameras([...exterior, ...interior]);
};

const normalizeCamera = (camera: Camera): Camera => {
  const resolvedType = camera.type ?? camera.camera_type ?? CAMERA_TYPES.EXTERIOR;
  const resolvedIpAddress = camera.ip_address || camera.http_config?.ip_address || "";
  const resolvedQueryParams = camera.query_params ?? camera.http_config?.query_params ?? "";
  const resolvedUsername = camera.username || camera.http_config?.username || "";
  const resolvedPassword = camera.password || camera.http_config?.password || "";
  const resolvedSkipRicoh = Boolean(
    camera.skip_ricoh_while_charging ?? camera.desired_settings?.skip_ricoh_while_charging
  );

  return {
    ...camera,
    cameraId: camera.cameraId ?? camera.id,
    id: camera.id ?? camera.cameraId,
    type: resolvedType,
    camera_type: resolvedType,
    brand: camera.brand || (resolvedType === CAMERA_TYPES.INTERIOR ? "theta" : "axis"),
    capture_protocol: camera.capture_protocol || (camera.brand?.toLowerCase() === "axis" ? "http" : "mqtt"),
    camera_position: Number(camera.camera_position) || 0,
    order: Number(camera.order ?? camera.sort_position) || Number(camera.camera_position) || 0,
    sort_position: Number(camera.sort_position ?? camera.order ?? camera.camera_position) || 0,
    ip_address: resolvedIpAddress,
    username: resolvedUsername,
    password: resolvedPassword,
    name: camera.name || "",
    query_params: resolvedQueryParams,
    tag: camera.tag || (resolvedType === CAMERA_TYPES.EXTERIOR ? CameraTags.Unassigned : ""),
    skip_ricoh_while_charging: resolvedSkipRicoh,
    is_under_carriage: Boolean(camera.is_under_carriage),
    http_config: {
      ...(camera.http_config || {}),
      ip_address: resolvedIpAddress,
      username: resolvedUsername,
      password: resolvedPassword,
      query_params: resolvedQueryParams,
    },
    desired_settings: {
      ...(camera.desired_settings || {}),
      skip_ricoh_while_charging: resolvedSkipRicoh,
    },
  };
};

const cameraSubtitle = (camera: Camera) => {
  if (camera.is_under_carriage) {
    return camera.tag || "Undercarriage";
  }

  if (camera.type === CAMERA_TYPES.EXTERIOR) {
    return camera.tag || CameraTags.Unassigned;
  }

  return camera.name || camera.username || "Interior camera";
};

const getAssignedConfigNames = (camera: Camera, configurations: Record<string, any>) => {
  const cameraId = getCameraId(camera);

  return Object.entries(configurations || {})
    .filter(([, config]) => {
      const cameraIds = config?.clientProcess?.cameras ?? config?.cameras ?? [];
      return cameraIds.map(String).includes(cameraId);
    })
    .map(([name]) => name);
};

const getPreviewUrl = (camera: Camera | null, systemCode?: string) => {
  if (!camera?.ip_address || !systemCode) {
    return "";
  }

  const hostIp = camera.ip_address.replace(/\./g, "-");
  const host = `${hostIp}.${systemCode}.bwicontrol.com`;
  const queryParams = camera.query_params?.trim();
  const queryString = queryParams
    ? queryParams.startsWith("?")
      ? queryParams
      : `?${queryParams}`
    : "";
  const previewPath = camera.brand?.toLowerCase() === "axis" ? "" : "/capture";

  return `http://${host}${previewPath}${queryString}`;
};

const normalizeBrand = (brand: "axis" | "bwi", camera: Camera): Camera => ({
  ...camera,
  brand,
  capture_protocol: brand === "axis" ? "http" : "mqtt",
});

const SystemHardware: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const accent = theme.palette.status.accent;
  const { selectedSystem } = useSession();
  const { formData, saveRawSystem } = useSystemsContext();
  const { listCamerasApi, createCameraApi, updateCameraApi, batchUpdateCamerasApi, deleteCameraApi } = useCameraApi();

  const [activeTab, setActiveTab] = useState<HardwareTab>("cameras");
  const [cameraView, setCameraView] = useState<CameraView>(CAMERA_TYPES.EXTERIOR);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [selectedCameraId, setSelectedCameraId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Camera | null>(null);
  const [mode, setMode] = useState<CameraMode>("edit");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [snackbar, setSnackbar] = useState(EMPTY_SNACK);
  const [previewCamera, setPreviewCamera] = useState<Camera | null>(null);

  const systemCode = selectedSystem?.systemCode;

  const filteredCameras = useMemo(
    () => sortCameras(cameras.filter((camera) => isCameraInView(camera, cameraView))),
    [cameraView, cameras]
  );

  const selectedCamera = useMemo(
    () => cameras.find((camera) => getCameraId(camera) === selectedCameraId) ?? null,
    [cameras, selectedCameraId]
  );

  const configEntries = useMemo(
    () => Object.entries(formData?.configurations || {}),
    [formData]
  );

  const assignedConfigNames = useMemo(
    () => (draft ? getAssignedConfigNames(draft, formData?.configurations || {}) : []),
    [draft, formData]
  );

  const previewUrl = useMemo(
    () => getPreviewUrl(previewCamera, systemCode),
    [previewCamera, systemCode]
  );

  const loadCameras = useCallback(async () => {
    if (!systemCode) {
      setCameras([]);
      setSelectedCameraId(null);
      setDraft(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const result = await listCamerasApi(systemCode);
      const normalized = normalizeExteriorPositions(result.map(normalizeCamera));
      setCameras(normalized);

      const nextSelected = normalized.find((camera) => isCameraInView(camera, cameraView));
      setSelectedCameraId((current) => {
        if (current && normalized.some((camera) => getCameraId(camera) === current)) {
          return current;
        }
        return nextSelected ? getCameraId(nextSelected) : null;
      });
    } catch (error) {
      console.error("Failed to load cameras:", error);
      setSnackbar({
        open: true,
        message: "Unable to load cameras for this system.",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [cameraView, listCamerasApi, systemCode]);

  useEffect(() => {
    loadCameras();
  }, [loadCameras]);

  useEffect(() => {
    if (!selectedCamera || mode === "add") {
      return;
    }

    setDraft(normalizeCamera(selectedCamera));
  }, [mode, selectedCamera]);

  useEffect(() => {
    if (filteredCameras.length === 0) {
      if (mode !== "add") {
        setSelectedCameraId(null);
        setDraft(null);
      }
      return;
    }

    if (selectedCameraId && filteredCameras.some((camera) => getCameraId(camera) === selectedCameraId)) {
      return;
    }

    setSelectedCameraId(getCameraId(filteredCameras[0]));
    setMode("edit");
  }, [filteredCameras, mode, selectedCameraId]);

  const handleDraftChange = <K extends keyof Camera>(key: K, value: Camera[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const handleTypeChange = (_: React.MouseEvent<HTMLElement>, nextView: CameraView | null) => {
    if (!nextView) return;
    setCameraView(nextView);
    setMode("edit");
    setDraft(null);
  };

  const handleSelectCamera = (camera: Camera) => {
    setSelectedCameraId(getCameraId(camera));
    setMode("edit");
    setDraft(normalizeCamera(camera));
  };

  const handleAddCamera = () => {
    setMode("add");
    setSelectedCameraId(null);
    setDraft(createDraftCamera(cameraView, cameras));
  };

  const persistExteriorOrder = async (nextCameras: Camera[]) => {
    const normalizedExterior = normalizeExteriorPositions(nextCameras).filter(
      (camera) => camera.type === CAMERA_TYPES.EXTERIOR
    );

    await batchUpdateCamerasApi(normalizedExterior, systemCode!);
  };

  const handleSave = async () => {
    if (!draft || !systemCode) {
      return;
    }

    const cameraToSave = normalizeCamera({
      ...draft,
      is_under_carriage:
        draft.type === CAMERA_TYPES.EXTERIOR ? cameraView === "undercarriage" : false,
    });
    const existing = cameras.find((camera) => getCameraId(camera) === getCameraId(cameraToSave));
    const nextCameras = normalizeExteriorPositions([
      ...cameras.filter((camera) => getCameraId(camera) !== getCameraId(cameraToSave)),
      cameraToSave,
    ]);

    setIsSaving(true);

    try {
      if (cameraToSave.type === CAMERA_TYPES.EXTERIOR) {
        const positionChanged =
          mode === "add" ||
          !existing ||
          Number(existing.camera_position || 0) !== Number(cameraToSave.camera_position || 0);

        if (positionChanged) {
          const normalizedExterior = nextCameras.filter((camera) => camera.type === CAMERA_TYPES.EXTERIOR);
          await batchUpdateCamerasApi(normalizedExterior, systemCode);
        } else {
          await updateCameraApi(cameraToSave, systemCode);
        }
      } else if (existing) {
        await updateCameraApi(cameraToSave, systemCode);
      } else {
        await createCameraApi(cameraToSave, systemCode);
      }

      await loadCameras();
      setMode("edit");
      setSelectedCameraId(getCameraId(cameraToSave));
      setSnackbar({
        open: true,
        message: existing ? "Camera updated." : "Camera created.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to save camera:", error);
      setSnackbar({
        open: true,
        message: "Unable to save camera changes.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft || !systemCode) {
      return;
    }

    const cameraId = draft.cameraId ?? draft.id;
    if (!cameraId) {
      return;
    }

    setIsSaving(true);

    try {
      const remaining = normalizeExteriorPositions(
        cameras.filter((camera) => getCameraId(camera) !== getCameraId(draft))
      );

      await deleteCameraApi(cameraId, systemCode);

      if (draft.type === CAMERA_TYPES.EXTERIOR && remaining.some((camera) => camera.type === CAMERA_TYPES.EXTERIOR)) {
        await persistExteriorOrder(remaining);
      }

      await loadCameras();
      setDraft(null);
      setSelectedCameraId(null);
      setMode("edit");
      setSnackbar({
        open: true,
        message: "Camera deleted.",
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to delete camera:", error);
      setSnackbar({
        open: true,
        message: "Unable to delete camera.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (mode === "add") {
      setDraft(selectedCamera ? normalizeCamera(selectedCamera) : null);
      setMode("edit");
      return;
    }

    setDraft(selectedCamera ? normalizeCamera(selectedCamera) : null);
  };

  const handlePreview = (camera: Camera) => {
    setPreviewCamera(camera);
  };

  const handleUpdateAllCameraBrands = async (brand: "axis" | "bwi") => {
    if (!systemCode || cameras.length === 0) {
      return;
    }

    setIsSaving(true);

    try {
      await batchUpdateCamerasApi(cameras.map((camera) => normalizeBrand(brand, camera)), systemCode);
      await loadCameras();
      setSnackbar({
        open: true,
        message: `Updated all cameras to ${brand === "axis" ? "Axis" : "BWI"}.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update all camera brands:", error);
      setSnackbar({
        open: true,
        message: "Unable to update all camera brands.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleConfigAssignmentChange = async (configName: string, checked: boolean) => {
    if (!draft || !formData?.systemCode) {
      return;
    }

    const cameraId = getCameraId(draft);
    if (!cameraId) {
      return;
    }

    const updatedSystem = structuredClone(formData);
    const config = updatedSystem.configurations?.[configName];
    if (!config) {
      return;
    }

    const currentIds = Array.isArray(config?.clientProcess?.cameras)
      ? config.clientProcess.cameras
      : Array.isArray(config?.cameras)
        ? config.cameras
        : [];

    const nextIds = checked
      ? Array.from(new Set([...currentIds.map(String), cameraId]))
      : currentIds.map(String).filter((id: string) => id !== cameraId);

    config.clientProcess = {
      ...(config.clientProcess || {}),
      cameras: nextIds,
    };
    config.cameras = nextIds;

    setIsSaving(true);

    try {
      await saveRawSystem(updatedSystem);
      setSnackbar({
        open: true,
        message: `Updated camera assignments for ${configName}.`,
        severity: "success",
      });
    } catch (error) {
      console.error("Failed to update camera assignments:", error);
      setSnackbar({
        open: true,
        message: "Unable to update configuration assignments.",
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedSystem) {
    return (
      <Typography variant="body1" sx={{ mt: 4 }}>
        Select a system to manage hardware.
      </Typography>
    );
  }

  return (
    <>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" sx={{ mb: 1 }}>
            System Hardware
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage physical devices tied to {selectedSystem.commonName}. Cameras are live now; other hardware can slot into this view later.
          </Typography>
        </Box>

        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            borderColor: alpha(accent, 0.22),
            background: `linear-gradient(180deg, ${alpha(accent, 0.05)} 0%, ${theme.palette.background.paper} 18%)`,
          }}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ px: 3, pt: 2.5 }}>
              <Tabs
                value={activeTab}
                onChange={(_, next) => setActiveTab(next)}
                sx={{
                  minHeight: 0,
                  "& .MuiTabs-indicator": {
                    backgroundColor: accent,
                    height: 3,
                    borderRadius: 999,
                  },
                }}
              >
                <Tab
                  value="cameras"
                  label="Cameras"
                  icon={<CameraAltRoundedIcon fontSize="small" />}
                  iconPosition="start"
                  sx={{
                    minHeight: 44,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                />
              </Tabs>
            </Box>

            <Divider />

            {activeTab === "cameras" && (
              <Box sx={{ p: { xs: 2, md: 3 } }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", md: "center" }}
                  justifyContent="space-between"
                  sx={{ mb: 3 }}
                >
                  <ToggleButtonGroup
                    exclusive
                    value={cameraView}
                    onChange={handleTypeChange}
                    sx={{
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        px: 2,
                        borderColor: alpha(accent, 0.45),
                        color: theme.palette.text.secondary,
                        "&.Mui-selected": {
                          backgroundColor: alpha(accent, 0.12),
                          color: accent,
                          borderColor: accent,
                        },
                      },
                    }}
                  >
                    <ToggleButton value={CAMERA_TYPES.EXTERIOR}>Exterior</ToggleButton>
                    <ToggleButton value="undercarriage">Undercarriage</ToggleButton>
                    <ToggleButton value={CAMERA_TYPES.INTERIOR}>Interior</ToggleButton>
                  </ToggleButtonGroup>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={`${filteredCameras.length} ${getCameraViewLabel(cameraView)} Cameras`}
                      variant="outlined"
                      sx={{ borderColor: alpha(accent, 0.35), color: theme.palette.text.secondary }}
                    />
                    <ButtonGroup
                      size="small"
                      variant="outlined"
                      disabled={isSaving || isLoading || cameras.length === 0}
                      sx={{
                        "& .MuiButton-root": {
                          borderColor: alpha(accent, 0.35),
                          color: accent,
                        },
                      }}
                    >
                      <Button onClick={() => handleUpdateAllCameraBrands("axis")}>All Axis</Button>
                      <Button onClick={() => handleUpdateAllCameraBrands("bwi")}>All BWI</Button>
                    </ButtonGroup>
                    <Button
                      variant="contained"
                      startIcon={<AddCircleRoundedIcon />}
                      onClick={handleAddCamera}
                      sx={{
                        backgroundColor: accent,
                        "&:hover": {
                          backgroundColor: accent,
                          opacity: 0.92,
                        },
                      }}
                    >
                      Add Camera
                    </Button>
                  </Stack>
                </Stack>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "minmax(320px, 420px) minmax(0, 1fr)",
                    gap: 3,
                    alignItems: "start",
                  }}
                >
                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: theme.palette.divider,
                      minHeight: 520,
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      {isLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                          <CircularProgress />
                        </Box>
                      ) : filteredCameras.length === 0 ? (
                        <Box
                          sx={{
                            minHeight: 460,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            px: 3,
                          }}
                        >
                          <Box>
                            <CameraAltRoundedIcon sx={{ fontSize: 42, color: alpha(accent, 0.7), mb: 1 }} />
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              No {getCameraViewLabel(cameraView).toLowerCase()} cameras yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Add the first camera for this system to start organizing hardware in UMS.
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Stack spacing={1.25}>
                          {filteredCameras.map((camera) => {
                            const isSelected =
                              mode === "edit" && getCameraId(camera) === getCameraId(selectedCamera ?? ({} as Camera));

                            return (
                              <Card
                                key={getCameraId(camera)}
                                variant="outlined"
                                onClick={() => handleSelectCamera(camera)}
                                sx={{
                                  cursor: "pointer",
                                  borderRadius: 2,
                                  borderColor: isSelected ? alpha(accent, 0.75) : theme.palette.divider,
                                  backgroundColor: isSelected ? alpha(accent, 0.08) : theme.palette.background.paper,
                                  transition: "background-color 150ms ease, border-color 150ms ease",
                                }}
                              >
                                <CardContent
                                  sx={{
                                    p: 2,
                                    "&:last-child": { pb: 2 },
                                    display: "grid",
                                    gridTemplateColumns: "56px minmax(0, 1fr) auto",
                                    gap: 1.5,
                                    alignItems: "center",
                                  }}
                                >
                                  <Box sx={{ color: camera.ip_address ? theme.palette.success.main : theme.palette.text.disabled }}>
                                    {camera.ip_address ? (
                                      <VideocamRoundedIcon sx={{ fontSize: 34 }} />
                                    ) : (
                                      <VideocamOffRoundedIcon sx={{ fontSize: 34 }} />
                                    )}
                                  </Box>

                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                      {camera.type === CAMERA_TYPES.EXTERIOR
                                        ? camera.is_under_carriage
                                          ? `Undercarriage ${camera.camera_position}`
                                          : `Camera ${camera.camera_position}`
                                        : camera.name || camera.username || "Interior Camera"}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" noWrap>
                                      {cameraSubtitle(camera)}
                                    </Typography>
                                  </Box>

                                      <Button
                                    variant="contained"
                                    size="small"
                                    startIcon={<PreviewRoundedIcon />}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handlePreview(camera);
                                    }}
                                    sx={{
                                      minWidth: 0,
                                      backgroundColor: accent,
                                      "&:hover": {
                                        backgroundColor: accent,
                                        opacity: 0.92,
                                      },
                                    }}
                                  >
                                    Preview
                                  </Button>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </Stack>
                      )}
                    </CardContent>
                  </Card>

                  <Card
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: theme.palette.divider,
                      minHeight: 520,
                    }}
                  >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                      {!draft ? (
                        <Box
                          sx={{
                            minHeight: 460,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                            px: 3,
                          }}
                        >
                          <Box>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                              Select a camera
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pick an existing camera from the list or add a new one to edit its hardware details.
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Stack spacing={2.25}>
                          <Box>
                            <Typography variant="h6">
                              {mode === "add"
                                ? draft.is_under_carriage
                                  ? "Add Undercarriage Camera"
                                  : "Add Camera"
                                : draft.is_under_carriage
                                  ? "Edit Undercarriage Camera"
                                  : "Edit Camera"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {draft.type === CAMERA_TYPES.EXTERIOR
                                ? draft.is_under_carriage
                                  ? "Undercarriage cameras use the exterior camera schema and stay grouped under their own hardware view."
                                  : "Exterior cameras are position-based and are re-numbered automatically when you change order."
                                : "Interior cameras can be stored without an exterior tag or position."}
                            </Typography>
                          </Box>

                          {draft.type === CAMERA_TYPES.EXTERIOR && (
                            <TextField
                              label="Camera Position"
                              type="number"
                              value={draft.camera_position}
                              onChange={(event) =>
                                handleDraftChange("camera_position", Math.max(1, Number(event.target.value) || 1))
                              }
                              inputProps={{ min: 1 }}
                              fullWidth
                            />
                          )}

                          <TextField
                            label="IP Address"
                            value={draft.ip_address}
                            onChange={(event) => handleDraftChange("ip_address", event.target.value)}
                            fullWidth
                          />

                          {draft.type === CAMERA_TYPES.EXTERIOR && (
                            <TextField
                              select
                              label="Camera Tag"
                              value={draft.tag}
                              onChange={(event) => handleDraftChange("tag", event.target.value)}
                              fullWidth
                            >
                              {Object.values(CameraTags).map((tag) => (
                                <MenuItem key={tag} value={tag}>
                                  {tag}
                                </MenuItem>
                              ))}
                            </TextField>
                          )}

                          <TextField
                            select
                            label="Brand"
                            value={draft.brand || ""}
                            onChange={(event) => handleDraftChange("brand", event.target.value)}
                            fullWidth
                          >
                            <MenuItem value="axis">Axis</MenuItem>
                            <MenuItem value="bwi">BWI</MenuItem>
                            <MenuItem value="theta">Theta</MenuItem>
                          </TextField>

                          {(draft.brand?.toLowerCase() === "axis" || draft.brand?.toLowerCase() === "bwi") && (
                            <>
                              <TextField
                                label="Username"
                                value={draft.username}
                                onChange={(event) => handleDraftChange("username", event.target.value)}
                                fullWidth
                              />

                              <TextField
                                label="Password"
                                type="password"
                                value={draft.password}
                                onChange={(event) => handleDraftChange("password", event.target.value)}
                                fullWidth
                              />
                            </>
                          )}

                          <TextField
                            label="Name"
                            value={draft.name || ""}
                            onChange={(event) => handleDraftChange("name", event.target.value)}
                            fullWidth
                          />

                          {draft.type === CAMERA_TYPES.EXTERIOR && (
                            <>
                              <TextField
                                label="Query Params"
                                helperText={
                                  draft.brand?.toLowerCase() === "bwi"
                                    ? draft.ip_address
                                      ? `Failover HTTP URL: http://${draft.ip_address}/capture`
                                      : "BWI uses MQTT first; these query params apply to the HTTP failover capture endpoint."
                                    : draft.ip_address
                                      ? `Base URL: http://${draft.ip_address}/axis-cgi/jpg/image.cgi`
                                      : "Axis query parameters will append to the standard image endpoint."
                                }
                                value={draft.query_params || ""}
                                onChange={(event) => handleDraftChange("query_params", event.target.value)}
                                fullWidth
                              />
                            </>
                          )}

                          {draft.type === CAMERA_TYPES.INTERIOR && (
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={Boolean(draft.skip_ricoh_while_charging)}
                                  onChange={() =>
                                    handleDraftChange(
                                      "skip_ricoh_while_charging",
                                      !draft.skip_ricoh_while_charging
                                    )
                                  }
                                />
                              }
                              label="Skip Ricoh While Charging"
                            />
                          )}

                          {mode === "edit" && configEntries.length > 0 && (
                            <>
                              <Divider />
                              <Box>
                                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                  Configurations
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                  Assign this camera to the configurations that should use it.
                                </Typography>
                                <Stack spacing={0.5}>
                                  {configEntries.map(([configName]) => {
                                    const checked = assignedConfigNames.includes(configName);

                                    return (
                                      <FormControlLabel
                                        key={configName}
                                        control={
                                          <Checkbox
                                            checked={checked}
                                            onChange={(event) =>
                                              handleConfigAssignmentChange(configName, event.target.checked)
                                            }
                                            disabled={isSaving}
                                          />
                                        }
                                        label={configName}
                                      />
                                    );
                                  })}
                                </Stack>
                              </Box>
                            </>
                          )}

                          <Divider />

                          <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            justifyContent="space-between"
                          >
                            <Button
                              color="error"
                              variant="text"
                              startIcon={<DeleteOutlineRoundedIcon />}
                              onClick={handleDelete}
                              disabled={mode === "add" || isSaving}
                              sx={{ alignSelf: { xs: "stretch", sm: "center" } }}
                            >
                              Delete Camera
                            </Button>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                              <Button onClick={handleCancel} disabled={isSaving}>
                                Cancel
                              </Button>
                              <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={isSaving}
                                sx={{
                                  minWidth: 140,
                                  backgroundColor: accent,
                                  "&:hover": {
                                    backgroundColor: accent,
                                    opacity: 0.92,
                                  },
                                }}
                              >
                                {isSaving ? <CircularProgress size={22} sx={{ color: "#fff" }} /> : "Save Camera"}
                              </Button>
                            </Stack>
                          </Stack>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(EMPTY_SNACK)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar(EMPTY_SNACK)}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={Boolean(previewCamera)}
        onClose={() => setPreviewCamera(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Camera Preview</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              This loads the live camera image from the `bwicontrol.com` host and will only work when that route is reachable.
            </Typography>
            <Box
              sx={{
                minHeight: 320,
                borderRadius: 2,
                overflow: "hidden",
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 1,
              }}
            >
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Camera preview"
                  sx={{
                    display: "block",
                    width: "100%",
                    height: { xs: 320, md: 420 },
                    maxWidth: "100%",
                    objectFit: "contain",
                    backgroundColor: "#000",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    height: 320,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 3,
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    This camera does not have enough connection info to build a preview URL.
                  </Typography>
                </Box>
              )}
            </Box>
            <TextField
              label="Preview URL"
              value={previewUrl}
              fullWidth
              InputProps={{ readOnly: true }}
            />
            {previewCamera?.query_params ? (
              <TextField
                label="Query Params"
                value={previewCamera.query_params}
                fullWidth
                InputProps={{ readOnly: true }}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewCamera(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SystemHardware;
